import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { 
  User, 
  Mail, 
  MapPin, 
  Building, 
  Briefcase, 
  DollarSign,
  Twitter,
  Linkedin,
  Save,
  CheckCircle,
  XCircle,
  Loader,
  Bell,
  Camera
} from 'lucide-react'
import { useProfile } from '@/hooks/useProfile'
import { UpdateProfileRequest } from '@/types/profile'
import { formatCurrency } from '@/utils'
import { Button, Input } from '@/components/atoms'
import { PageTemplate } from '@/components/templates'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { ProfilePreferencesDisplay, InterestManagement, IndustriesManagement, ProfileTabs } from '@/components/molecules'
import InquiriesTable from '@/components/molecules/InquiriesTable'
import { CONTAINER_SIZES } from '@/constants'
import { notificationService } from '@/services/notificationService'
import type { NotificationSettings } from '@/types/notifications'

type ActiveTab = 'profile' | 'interests' | 'inquiries' | 'settings'

const ProfileScreen = () => {
  const { profile, loading, updating, uploadingPicture, error, updateProfile, uploadProfilePicture } = useProfile()
  
  // Form state
  const [formData, setFormData] = useState<UpdateProfileRequest>({})
  const [successMessage, setSuccessMessage] = useState('')
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState<ActiveTab>('profile')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Settings state
  const [isSettingsLoading, setIsSettingsLoading] = useState(true)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null)

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        country: profile.country || '',
        preferred_investment_amount_min: profile.preferred_investment_amount_min,
        preferred_investment_amount_max: profile.preferred_investment_amount_max,
        x_account: profile.x_account || '',
        linkedin: profile.linkedin || '',
        company_name: profile.company_name || '',
        job_title: profile.job_title || '',
        email: profile.email,
        // New preference fields
        companies_of_interest_buy: profile.companies_of_interest_buy || [],
        companies_of_interest_sell: profile.companies_of_interest_sell || [],
        industries_of_interest: profile.industries_of_interest || []
      })
    }
  }, [profile])

  // Load settings when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings') {
      loadSettings()
    }
  }, [activeTab])

  const loadSettings = async () => {
    try {
      setIsSettingsLoading(true)
      setSettingsError(null)
      const settings = await notificationService.getSettings()
      setNotificationSettings(settings)
    } catch (err) {
      setSettingsError('Failed to load settings')
      console.error(err)
    } finally {
      setIsSettingsLoading(false)
    }
  }

  // Handle form field changes
  const handleInputChange = (field: keyof UpdateProfileRequest, value: string | number | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  // Handle profile picture upload
  const handleProfilePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      await uploadProfilePicture(file)
      setSuccessMessage('Profile picture updated successfully!')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err) {
      setValidationErrors(prev => ({ 
        ...prev, 
        profile_picture: err instanceof Error ? err.message : 'Failed to upload profile picture' 
      }))
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Get profile picture URL
  const getProfilePictureUrl = () => {
    return profile?.profile_picture_url
  }

  // Get user initials for fallback avatar
  const getUserInitials = () => {
    const first = profile?.first_name?.[0] || ''
    const last = profile?.last_name?.[0] || ''
    return (first + last).toUpperCase() || 'U'
  }

  // Handle interest management
  const handleAddBuyInterest = async (newInterests: string[]) => {
    const updatedInterests = [...(formData.companies_of_interest_buy || []), ...newInterests]
    const updatedData = { ...formData, companies_of_interest_buy: updatedInterests }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const handleRemoveBuyInterest = async (interestToRemove: string) => {
    const updatedInterests = (formData.companies_of_interest_buy || []).filter(i => i !== interestToRemove)
    const updatedData = { ...formData, companies_of_interest_buy: updatedInterests }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const handleAddSellInterest = async (newInterests: string[]) => {
    const updatedInterests = [...(formData.companies_of_interest_sell || []), ...newInterests]
    const updatedData = { ...formData, companies_of_interest_sell: updatedInterests }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const handleRemoveSellInterest = async (interestToRemove: string) => {
    const updatedInterests = (formData.companies_of_interest_sell || []).filter(i => i !== interestToRemove)
    const updatedData = { ...formData, companies_of_interest_sell: updatedInterests }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const handleUpdateIndustries = async (industries: string[]) => {
    const updatedData = { ...formData, industries_of_interest: industries }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  // Handle notification settings
  const handleNotificationToggle = async (enabled: boolean) => {
    try {
      // Optimistic update
      if (notificationSettings) {
        setNotificationSettings({
          ...notificationSettings,
          email_notifications: enabled
        })
      }
      
      // Make API call
      if (notificationSettings) {
        const updated = await notificationService.updateSettings({
          ...notificationSettings,
          email_notifications: enabled
        })
        setNotificationSettings(updated)
      }
    } catch (err) {
      // Revert optimistic update on error
      if (notificationSettings) {
        setNotificationSettings({
          ...notificationSettings,
          email_notifications: !enabled
        })
      }
      setSettingsError('Failed to update notification settings')
      console.error(err)
    }
  }

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format'
    }

    if (formData.preferred_investment_amount_min && formData.preferred_investment_amount_max) {
      if (formData.preferred_investment_amount_min >= formData.preferred_investment_amount_max) {
        errors.preferred_investment_amount_max = 'Maximum amount must be greater than minimum'
      }
    }

    if (formData.linkedin && !formData.linkedin.includes('linkedin.com')) {
      errors.linkedin = 'Please enter a valid LinkedIn URL'
    }

    if (formData.x_account && !formData.x_account.startsWith('@')) {
      errors.x_account = 'X account should start with @'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await updateProfile(formData)
      setSuccessMessage('Profile updated successfully!')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err) {
      // Error is already handled in the hook
    }
  }

  if (loading) {
    return (
      <PageTemplate>
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center gap-3">
            <Loader className="animate-spin text-primary-500" size={24} />
            <span className="text-[var(--text)]">Loading profile...</span>
          </div>
        </div>
      </PageTemplate>
    )
  }

  if (error && !profile) {
    return (
      <PageTemplate>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <XCircle className="text-red-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-[var(--text)] mb-2">Error Loading Profile</h2>
            <p className="text-[var(--text)] opacity-60">{error}</p>
          </div>
        </div>
      </PageTemplate>
    )
  }

  return (
    <PageTemplate
      title="Profile & Settings"
      subtitle="Manage your personal information, interests, and preferences"
      maxWidth={CONTAINER_SIZES.WIDE}
    >
      <div className="space-y-6">
        
        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 flex items-center gap-2 rounded-lg"
          >
            <CheckCircle size={18} />
            <span className="text-sm">{successMessage}</span>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 flex items-center gap-2 rounded-lg"
          >
            <XCircle size={18} />
            <span className="text-sm">{error}</span>
          </motion.div>
        )}

        {/* Tab Navigation */}
        <ProfileTabs 
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Profile Information Tab */}
        {activeTab === 'profile' && (
          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Profile Picture Section */}
                <div className="flex flex-col items-center pb-6 border-b border-border">
                  <div className="relative group">
                    {/* Avatar */}
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-[var(--background-lighter)] border-4 border-white dark:border-gray-800 shadow-lg">
                      {getProfilePictureUrl() ? (
                        <img 
                          src={getProfilePictureUrl()} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                          <span className="text-2xl font-bold text-primary">
                            {getUserInitials()}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Upload Overlay */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPicture}
                      className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer disabled:cursor-not-allowed"
                    >
                      {uploadingPicture ? (
                        <Loader className="w-6 h-6 text-white animate-spin" />
                      ) : (
                        <Camera className="w-6 h-6 text-white" />
                      )}
                    </button>
                    
                    {/* Hidden File Input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleProfilePictureChange}
                      className="hidden"
                    />
                  </div>
                  
                  {/* Upload Button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                    className="mt-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                  >
                    {uploadingPicture ? 'Uploading...' : 'Change Photo'}
                  </button>
                  
                  {/* Upload Hint */}
                  <p className="mt-1 text-xs text-[var(--text)] opacity-50">
                    JPEG, PNG or WebP. Max 25MB.
                  </p>
                  
                  {/* Upload Error */}
                  {validationErrors.profile_picture && (
                    <p className="mt-2 text-xs text-red-500">
                      {validationErrors.profile_picture}
                    </p>
                  )}
                </div>

                {/* Basic Information */}
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text)] mb-4 flex items-center gap-2">
                    <User className="text-[var(--text)]" size={20} />
                    Basic Information
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        First Name
                      </label>
                      <Input
                        type="text"
                        value={formData.first_name || ''}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        placeholder="Enter your first name"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<User className="w-4 h-4" />}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        Last Name
                      </label>
                      <Input
                        type="text"
                        value={formData.last_name || ''}
                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                        placeholder="Enter your last name"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<User className="w-4 h-4" />}
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text)] mb-4 flex items-center gap-2">
                    <Mail className="text-[var(--text)]" size={20} />
                    Contact Information
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        Email Address
                      </label>
                      <Input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="bg-[var(--background-lighter)]"
                        leftElement={<Mail className="w-4 h-4" />}
                        error={validationErrors.email}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        Country
                      </label>
                      <Input
                        type="text"
                        value={formData.country || ''}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        placeholder="Enter your country"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<MapPin className="w-4 h-4" />}
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text)] mb-4 flex items-center gap-2">
                    <Building className="text-[var(--text)]" size={20} />
                    Professional Information
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        Company Name
                      </label>
                      <Input
                        type="text"
                        value={formData.company_name || ''}
                        onChange={(e) => handleInputChange('company_name', e.target.value)}
                        placeholder="Enter your company"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<Building className="w-4 h-4" />}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        Job Title
                      </label>
                      <Input
                        type="text"
                        value={formData.job_title || ''}
                        onChange={(e) => handleInputChange('job_title', e.target.value)}
                        placeholder="Enter your job title"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<Briefcase className="w-4 h-4" />}
                      />
                    </div>
                  </div>
                </div>

                {/* Investment Preferences */}
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text)] mb-4 flex items-center gap-2">
                    <DollarSign className="text-[var(--text)]" size={20} />
                    Investment Preferences
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        Minimum Investment Amount
                      </label>
                      <Input
                        type="number"
                        value={formData.preferred_investment_amount_min || ''}
                        onChange={(e) => handleInputChange('preferred_investment_amount_min', parseFloat(e.target.value))}
                        placeholder="Enter minimum amount"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<DollarSign className="w-4 h-4" />}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        Maximum Investment Amount
                      </label>
                      <Input
                        type="number"
                        value={formData.preferred_investment_amount_max || ''}
                        onChange={(e) => handleInputChange('preferred_investment_amount_max', parseFloat(e.target.value))}
                        placeholder="Enter maximum amount"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<DollarSign className="w-4 h-4" />}
                        error={validationErrors.preferred_investment_amount_max}
                      />
                    </div>
                  </div>
                </div>

                {/* Social Accounts */}
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text)] mb-4 flex items-center gap-2">
                    <Twitter className="text-[var(--text)]" size={20} />
                    Social Accounts
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        X Account
                        {profile?.x_account_verified && (
                          <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />
                        )}
                      </label>
                      <Input
                        type="text"
                        value={formData.x_account || ''}
                        onChange={(e) => handleInputChange('x_account', e.target.value)}
                        placeholder="@username"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<Twitter className="w-4 h-4" />}
                        error={validationErrors.x_account}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[var(--text)] mb-2">
                        LinkedIn
                        {profile?.linkedin_account_verified && (
                          <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />
                        )}
                      </label>
                      <Input
                        type="url"
                        value={formData.linkedin || ''}
                        onChange={(e) => handleInputChange('linkedin', e.target.value)}
                        placeholder="https://linkedin.com/in/username"
                        className="bg-[var(--background-lighter)]"
                        leftElement={<Linkedin className="w-4 h-4" />}
                        error={validationErrors.linkedin}
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={updating}
                    isLoading={updating}
                    leftIcon={!updating ? <Save size={16} /> : undefined}
                  >
                    {updating ? 'Updating...' : 'Update Profile'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Investment Interests Tab */}
        {activeTab === 'interests' && (
          <div className="space-y-6">
            {/* Buy Interests */}
            <Card>
              <CardContent className="p-6">
                <InterestManagement
                  title="Assets You Want to Buy"
                  interests={formData.companies_of_interest_buy || []}
                  onAdd={handleAddBuyInterest}
                  onRemove={handleRemoveBuyInterest}
                  placeholder="e.g., Ethereum, Solana, Polygon"
                  loading={updating}
                />
              </CardContent>
            </Card>

            {/* Sell Interests */}
            <Card>
              <CardContent className="p-6">
                <InterestManagement
                  title="Assets You Want to Sell"
                  interests={formData.companies_of_interest_sell || []}
                  onAdd={handleAddSellInterest}
                  onRemove={handleRemoveSellInterest}
                  placeholder="e.g., Bitcoin, Cardano"
                  loading={updating}
                />
              </CardContent>
            </Card>

            {/* Industries */}
            <Card>
              <CardContent className="p-6">
                <IndustriesManagement
                  selectedIndustries={formData.industries_of_interest || []}
                  onUpdate={handleUpdateIndustries}
                  loading={updating}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <InquiriesTable />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Card>
            <CardContent className="p-6">
              {isSettingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="animate-spin text-primary-500" size={24} />
                  <span className="ml-2 text-[var(--text)]">Loading settings...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {settingsError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      {settingsError}
                    </div>
                  )}

                  {/* Notification Settings */}
                  <div>
                    <h2 className="text-xl font-semibold text-[var(--text)] mb-4 flex items-center gap-2">
                      <Bell className="text-[var(--text)]" size={20} />
                      Notification Preferences
                    </h2>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                        <div>
                          <h3 className="font-medium text-[var(--text)]">Email Notifications</h3>
                          <p className="text-sm text-[var(--text)] opacity-60">
                            Receive notifications about your investments and market updates
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={notificationSettings?.email_notifications || false}
                            onCheckedChange={handleNotificationToggle}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Settings */}
                  <div>
                    <h2 className="text-xl font-semibold text-[var(--text)] mb-4">
                      Additional Settings
                    </h2>
                    <p className="text-[var(--text)] opacity-60">
                      More settings will be available here in future updates.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Profile Summary */}
        {profile && activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Profile Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Card variant="elevated">
                <CardContent className="p-4 text-center">
                  <div className="text-lg font-semibold text-[var(--text)]">
                    {formatCurrency(profile.preferred_investment_amount_min)} - {formatCurrency(profile.preferred_investment_amount_max)}
                  </div>
                  <div className="text-sm text-[var(--text)] opacity-60">Investment Range</div>
                </CardContent>
              </Card>
              <Card variant="elevated">
                <CardContent className="p-4 text-center">
                  <div className="text-lg font-semibold text-[var(--text)]">
                    {profile.company_name || 'Not specified'}
                  </div>
                  <div className="text-sm text-[var(--text)] opacity-60">Company</div>
                </CardContent>
              </Card>
              <Card variant="elevated">
                <CardContent className="p-4 text-center">
                  <div className="text-lg font-semibold text-[var(--text)]">
                    {profile.country || 'Not specified'}
                  </div>
                  <div className="text-sm text-[var(--text)] opacity-60">Location</div>
                </CardContent>
              </Card>
              <Card variant="elevated">
                <CardContent className="p-4 text-center">
                  <div className="text-lg font-semibold text-[var(--text)]">
                    {(profile.industries_of_interest || []).length}
                  </div>
                  <div className="text-sm text-[var(--text)] opacity-60">Industries</div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </PageTemplate>
  )
}

export default ProfileScreen
